import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import WindowViewModelType from '@/ViewModels/Controls/WindowViewModel';
import DropDownListType from '@/Controls/DropDownList';
import Utilities from '@/Utility/Utilities';
import RADApplication from '@/Management/RADApplication';

export default class Window extends Control {

    private DropDownList = ComponentManager.getControl<typeof DropDownListType>('DropDownList');
    protected jQueryControl: JQuery;
    private dropDownList: DropDownListType;
    private widgetIsClosing: boolean;

    /**
     * @override
     */
    public widget: KnockoutObservable<kendo.ui.Window>;

    public constructor(protected viewModel: WindowViewModelType, protected element?: JQuery) {
        super(viewModel, element);

        $("div[data-region=\"Body\"]").removeClass("hidden-print");
        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div>');
        this.jQueryControl.css('visibility', 'hidden');

        this.applyBindings(this.jQueryControl, {
            kendoWindow: {
                animation: this.viewModel.animation,

                visible: false,

                draggable: this.viewModel.isDraggable,

                resizable: this.viewModel.isResizable,

                appendTo: this.definePureComputed({
                    read: () => {
                        return this.getElementToAppended();
                    }
                }),

                isOpen: false,

                modal: this.viewModel.isModal,

                title: this.definePureComputed({
                    read: () => {
                        const headline = this.viewModel.headline;

                        if (!headline)
                            return '';

                        return `${headline.prefix?.caption() || ''}${headline.caption()}${headline.postfix?.caption() || ''}`;
                    }
                }),

                width: Utilities.isMobileView() ? '80vw' : this.viewModel.width,

                height: this.viewModel.height,

                maxWidth: this.viewModel.maxWidth,

                maxHeight: this.viewModel.maxHeight,

                activate: (event) => this.onActivate(event),

                actions: this.definePureComputed({
                    read: () => {
                        let actions = [];

                        if (this.viewModel.isDisposed)
                            return actions;

                        if (this.viewModel.allowMinimize()) {
                            actions.push('Minimize');
                        }

                        if (this.viewModel.allowClose()) {
                            actions.push('Close');
                        }

                        return actions;
                    }
                }),

                widget: this.widget,

                open: () => {
                    if (!this.viewModel.isOpen()) {
                        this.viewModel.isOpen(true);
                    }
                    if (!this.viewModel.isLoginWindow()) {
                        $("div[data-region=\"Body\"]").addClass("hidden-print");
                    }
                    if (this.viewModel.isModal())
                        RADApplication.getInstance().increaseModalWindowOpenCounter();
                },

                close: () => {
                    this.widgetIsClosing = true;
                    if (this.viewModel.isOpen()) {
                        this.viewModel.isOpen(false);
                    }
                    $("div[data-region=\"Body\"]").removeClass("hidden-print");
                    if (this.viewModel.isModal())
                        RADApplication.getInstance().decreaseModalWindowOpenCounter();
                    this.widgetIsClosing = false;

                    // Der FF verliert manchmal einige Hintergr�nde nach dem Schlie�en bildschirmf�llender Ansichten
                    $("svg [fill]").each((index: number, element: HTMLElement) => {
                        var fill = $(element).attr("fill");
                        $(element).attr("fill", "").attr("fill", fill);
                    });

                },

                minimize: this.onMinimize.bind(this),

                autoFocus: this.viewModel.autoFocus
            }
        });

        return this.jQueryControl;
    }

    protected onWidgetCreatedCallback(widget: kendo.ui.Window): void {
        this.applyBindings(widget.wrapper, {
            controlName: this.viewModel.controlName ?? 'window',

            technicalName: this.viewModel.technicalName,

            loader: this.viewModel.isBusy,

            controlIdent: this.identifier,

            tooltip: this.viewModel.tooltip,

            invalid: this.viewModel.invalidationText,

            styles: this.definePureComputed({
                read: () => {
                    var styles = [];

                    if (this.viewModel.isLoginWindow() === true) {
                        styles.push('login');
                    } else if (this.viewModel.isSearchListViewWindow() === true) {
                        styles.push('searchlist');
                    } else if (this.viewModel.isMessageBox() === true) {
                        styles.push('messagebox');
                    } else {
                        styles.push('default');
                    }

                    return styles;
                }
            }),

            labelledBy: this.definePureComputed({
                read: () => {
                    return this.viewModel.isMessageBox() === true ? widget.wrapper.find('.k-window-title') : undefined;
                }
            }),

            states: this.definePureComputed({
                read: () => {
                    var states = [];

                    if (this.viewModel.isRequired() === true) {
                        states.push('required');
                    }

                    if (this.viewModel.isValid() === true) {
                        states.push('valid');
                    } else {
                        states.push('invalid');
                    }

                    return states;
                }
            }),

            attr: {
                role: this.definePureComputed({
                    read: () => {
                        var role = null;

                        if (this.viewModel.isMessageBox() === true) {
                            role = 'alertdialog';
                        }
                        else if (!this.viewModel.isLoginWindow())
                            role = 'dialog';

                        return role;
                    }
                })
            }
        });

        widget.center();

        this.defineComputed(() => {
            if (this.viewModel.isOpen() === true) {
                this.async(() => {
                    this.viewModel.isMinimized(false);
                    widget.restore();
                    widget.open();
                    widget.center();

                    if (this.viewModel.isMaximized() === true) {
                        widget.maximize();
                    }

                    if (this.viewModel.centerOnWindowResize) {
                        this.registerWindowResizeHandler();
                    }
                });
            } else {
                if (!this.widgetIsClosing) {
                    widget.close();
                }
                this.unregisterWindowResizeHandler();
            }
        });

        this.defineSubscription(this.viewModel.triggerMinimize, (_) => widget.minimize());
        this.defineSubscription(this.viewModel.triggerRestore, (_) => { widget.restore(); this.onRestore(); });

        this.addEventHandler('keyup', widget.wrapper, (ev) => {
            if (!this.isDisposed && ev.keyCode == 13) {
                $(widget.wrapper).find('.c-state-primary').mouseup();
            }
        });

        if (this.viewModel.dropDownList != null) {
            this.dropDownList = new this.DropDownList(this.viewModel.dropDownList);
            var tuple = this.dropDownList.getTuple();

            tuple.control.appendTo(widget.wrapper.find('.k-window-titlebar'));
        }

        if (this.viewModel.onOpen != null) {
            widget.bind('open', this.viewModel.onOpen);
        }

        if (this.viewModel.onClose != null) {
            widget.bind('close', this.viewModel.onClose);
        }

        if (this.viewModel.content != null) {
            this.viewModel.content(widget.element);
        }

        if (!Utilities.isNullOrUndefined(this.viewModel.locator)) {
            this.applyBindings(widget.wrapper, {
                locator: this.viewModel.locator
            });

            var titleBar = widget.wrapper.find('.k-window-titlebar');
            if (titleBar.length === 1) {
                this.applyBindings(titleBar, {
                    locator: this.defineComputed(() => {
                        if (!Utilities.isNullOrUndefined(this.viewModel.locator()))
                            return this.viewModel.locator() + '.titlebar';
                        return null;
                    })
                });

                var titleSpan = titleBar.children('.k-window-title');
                if (titleSpan.length === 1) {
                    this.applyBindings(titleSpan, {
                        locator: this.defineComputed(() => {
                            if (!Utilities.isNullOrUndefined(this.viewModel.locator()))
                                return this.viewModel.locator() + '.title';
                            return null;
                        })
                    });
                }

                this.defineComputed(() => {
                    if (this.viewModel.isDisposed === false && this.viewModel.allowClose() === true) {
                        var actions = titleBar.find('> .k-window-actions > a');
                        if (actions.length === 1) {
                            // Schließen-Button
                            this.applyBindings(actions, {
                                autoLocator: this.defineComputed(() => {
                                    if(!Utilities.isNullOrUndefined(this.viewModel.locator()))
                                        return this.viewModel.locator() + '.close-action';
                                    return null;
                                })
                            });
                        }
                    }
                });
            }
        }

        this.registerRestoreHandler();
    }

    private focusUsernameOrPasswordInput(): void {
        const $usernameInput = this.element?.find('input[name="username"]') as JQuery<HTMLInputElement>;
        const $passwordInput = this.element?.find('input[name="password"]') as JQuery<HTMLInputElement>;

        if (!$usernameInput.length || !$passwordInput.length) {
            return;
        }

        const username = $usernameInput.val();
        const password = $passwordInput.val();

        if (username && !password) {
            $passwordInput.focus();
        } else {
            $usernameInput.focus();
        }
    }

    protected getOpenAnimationDuration(): number | undefined {
        const kendoWindow = this.element?.data('kendoWindow');
        if (!kendoWindow) {
            return undefined;
        }

        const animation = kendoWindow.options.animation;
        if (typeof animation === 'boolean') {
            return undefined;
        }

        const duration = animation.open?.duration;
        if (duration == null) {
            return undefined;
        }

        return duration;
    }

    /**
     * Registriert einen Event-Handler für das Restore
     * Workaround, da es kein restore-Event gibt
     * https://github.com/telerik/kendo-ui-core/issues/1694
     */
    private registerRestoreHandler(): void {
        this.addEventHandler('click', this.widget().wrapper,
            (event) => {
                const target = $(event.target);
                if (target.is('.k-i-window-restore') || target.children('.k-i-window-restore')[0]) {
                    this.onRestore();
                }
            }, '.k-window-action');
    }

    protected onActivate(event: any): void {
        // https://www.telerik.com/forums/window-don-t-get-focus-with-jquery-3-6-0
        // in dem Forum wurde auf wrapper[0] verwiesen, ist bei uns aber das falsche Element.
        event?.sender?.element[0]?.focus();

        // TODO: Das würde eigentlich in eine "LoginWindow"-Unterklasse gehören...
        if (this.viewModel.isLoginWindow())
            this.focusUsernameOrPasswordInput();

        if (this.viewModel.onActivate)
            this.viewModel.onActivate();
    }

    private onResizeWindow() {
        if (this.isDisposed !== false) {
            return;
        }

        const widget = this.widget();
        if (widget == null) {
            return;
        }

        widget.center();
    }

    /**
     * Wird beim Minimieren ausgelöst
     */
    private onMinimize() {
        this.viewModel.isMinimized(true);
    }

    /**
     * Wird beim Wiederherstellen ausgelöst
     */
    private onRestore() {
        this.viewModel.isMinimized(false);
    }

    private registerWindowResizeHandler() {
        this.addEventHandler('resize', $(window), this.onResizeWindow.bind(this));
    }

    private unregisterWindowResizeHandler() {
        this.removeEventHandler('resize', $(window));
    }

    /**
     * Welchem Element soll das Fenster hinzugefügt werden
     */
    protected getElementToAppended(): JQuery<HTMLElement> {
        return this.element != null ? this.element.closest('[data-view]') : $('[data-region=Modal]');
    }

    public center() {
        if (!Utilities.isNullOrUndefined(this.widget()))
            this.widget().center();
    }

    protected disposeInternal() {
        if (this.viewModel.content != null) {
            this.viewModel.content(null);
        }

        super.disposeInternal();
    }
}

ComponentManager.setControl('Window', Window, 'RADPresentation.HTMLClient.DefaultViewExtensions');